import React, { useMemo, useState } from "react";
import { useList } from "@refinedev/core";
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Button,
  Space,
  Typography,
  Empty,
  DatePicker,
} from "antd";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  DownloadOutlined,
  ReloadOutlined,
  UserOutlined,
  ClusterOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Sandbox } from "../../types/sandbox";
import { LoadingSkeleton } from "../../components/LoadingSkeleton";
import dayjs, { Dayjs } from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_COLORS = ["#52c41a", "#1890ff", "#faad14", "#ff4d4f", "#d9d9d9"];

export const AnalyticsPage: React.FC = () => {
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d" | "custom">("30d");
  const [customDateRange, setCustomDateRange] = useState<[Dayjs, Dayjs] | null>(null);

  const { data: sandboxes, isLoading, refetch } = useList<Sandbox>({
    resource: "sandboxes",
    pagination: { pageSize: 1000 }, // Get all for analytics
  });

  // Calculate date range
  const getDateRange = () => {
    const now = dayjs();
    if (timeRange === "custom" && customDateRange) {
      return {
        start: customDateRange[0],
        end: customDateRange[1],
      };
    }
    const daysMap: Record<string, number> = { "7d": 7, "30d": 30, "90d": 90 };
    return {
      start: now.subtract(daysMap[timeRange] || 30, "day"),
      end: now,
    };
  };

  const dateRange = getDateRange();

  // Filter sandboxes by date range
  const filteredSandboxes = useMemo(() => {
    if (!sandboxes?.data) return [];
    return sandboxes.data.filter((s) => {
      const createdAt = dayjs(s.created_at);
      return createdAt.isAfter(dateRange.start) && createdAt.isBefore(dateRange.end);
    });
  }, [sandboxes?.data, dateRange]);

  // User activity (top 10 users by sandbox count)
  const userActivity = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredSandboxes.forEach((s) => {
      counts[s.created_by] = (counts[s.created_by] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([user, count]) => ({ user, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredSandboxes]);

  // Cluster distribution
  const clusterDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredSandboxes.forEach((s) => {
      counts[s.spec.cluster] = (counts[s.spec.cluster] || 0) + 1;
    });
    return Object.entries(counts).map(([cluster, count]) => ({ cluster, count }));
  }, [filteredSandboxes]);

  // Sandbox creation trend (last N days)
  const creationTrend = useMemo(() => {
    const days = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
    const trend: { date: string; count: number }[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = dayjs().subtract(i, "day").format("MMM DD");
      const count = filteredSandboxes.filter((s) =>
        dayjs(s.created_at).isSame(dayjs().subtract(i, "day"), "day")
      ).length;
      trend.push({ date, count });
    }

    return trend;
  }, [filteredSandboxes, timeRange]);

  // Status distribution
  const statusDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredSandboxes.forEach((s) => {
      counts[s.status] = (counts[s.status] || 0) + 1;
    });
    return Object.entries(counts).map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
    }));
  }, [filteredSandboxes]);

  // Average sandbox lifetime (in hours)
  const avgLifetime = useMemo(() => {
    const completedSandboxes = filteredSandboxes.filter(
      (s) => s.status === "ready" || s.status === "failed"
    );
    if (completedSandboxes.length === 0) return 0;

    const totalLifetime = completedSandboxes.reduce((sum, s) => {
      const created = dayjs(s.created_at);
      const updated = dayjs(s.updated_at);
      return sum + updated.diff(created, "hour", true);
    }, 0);

    return totalLifetime / completedSandboxes.length;
  }, [filteredSandboxes]);

  // Success rate
  const successRate = useMemo(() => {
    const total = filteredSandboxes.length;
    if (total === 0) return 0;
    const successful = filteredSandboxes.filter((s) => s.status === "ready").length;
    return (successful / total) * 100;
  }, [filteredSandboxes]);

  // Most active users count
  const activeUsersCount = useMemo(() => {
    return new Set(filteredSandboxes.map((s) => s.created_by)).size;
  }, [filteredSandboxes]);

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Sandbox ID",
      "Status",
      "Cluster",
      "Created By",
      "Created At",
      "Updated At",
      "URL",
    ];
    const rows = filteredSandboxes.map((s) => [
      s.id,
      s.status,
      s.spec.cluster,
      s.created_by,
      s.created_at,
      s.updated_at,
      s.url,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows].map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `sandbox-analytics-${dayjs().format("YYYY-MM-DD")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return <LoadingSkeleton type="analytics" />;
  }

  if (!sandboxes?.data || sandboxes.data.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="No sandbox data available for analytics" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>Analytics Dashboard</Title>
          <Text type="secondary">
            Showing data from {dateRange.start.format("MMM DD, YYYY")} to{" "}
            {dateRange.end.format("MMM DD, YYYY")}
          </Text>
        </Col>
        <Col>
          <Space>
            <Select
              value={timeRange}
              onChange={setTimeRange}
              style={{ width: 120 }}
              options={[
                { label: "Last 7 Days", value: "7d" },
                { label: "Last 30 Days", value: "30d" },
                { label: "Last 90 Days", value: "90d" },
                { label: "Custom Range", value: "custom" },
              ]}
            />
            {timeRange === "custom" && (
              <RangePicker
                value={customDateRange}
                onChange={(dates) => setCustomDateRange(dates as [Dayjs, Dayjs])}
              />
            )}
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button icon={<DownloadOutlined />} onClick={exportToCSV}>
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Sandboxes"
              value={filteredSandboxes.length}
              prefix={<ClusterOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Users"
              value={activeUsersCount}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={successRate.toFixed(1)}
              suffix="%"
              valueStyle={{ color: successRate > 80 ? "#3f8600" : "#cf1322" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg. Lifetime"
              value={avgLifetime.toFixed(1)}
              suffix="hrs"
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Row 1 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="Sandbox Creation Trend">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={creationTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#1890ff" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Status Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusDistribution.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 2 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Top Users by Sandbox Count">
            {userActivity.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={userActivity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="user" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#52c41a" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No user data available" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Cluster Distribution">
            {clusterDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={clusterDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ cluster, percent }) => `${cluster}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {clusterDistribution.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No cluster data available" />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
